/**
 * ⭐ F187 — THE JOURNEY, on the appointment dialog (item 104, Decisions 251, 358)
 *
 * > Pete, 2026-09-28: *"a cool graphical depiction of this using our truck icon and
 * > others to layout the whole dock to stock scenario"* → 2026-09-30 *"Both places"*,
 * > *"Inbound + outbound"*, progress from *"Receipt line times"*.
 *
 * The steps come from Pipeline's builder (journey.ts) — the same one behind
 * Pipeline's dock-to-stock drill — so this draws, it never decides. The board's own
 * marks: the truck, the spinning work mark (WorkTruckMark), the done tick.
 *
 *   dark   = done          grey = not reached yet       dashed = no time recorded
 *   amber  = in progress   orange = past its target (the dock-to-stock / on-time deadline)
 *
 * ⚠️ A step with no recorded time says so; nothing is interpolated. Times are shown
 * in the facility's own zone.
 */
import { Fragment, useEffect, useState } from 'react';
import { ClipboardCheck, DoorOpen, FileText, Package, PackageCheck, Truck, User } from 'lucide-react';
import { WorkTruckMark, WORK_MARK_SPIN_SECONDS } from '@/components/WorkTruckMark';
import { cn } from '@/lib/utils';
import { getAppointmentJourney, type JourneyDto, type JourneyStepDto, type JourneyTimelineDto } from '@/api/client';

function fmt(v: string | null | undefined, tz: string | null): string {
    if (!v) return '';
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    const o: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' };
    try { return d.toLocaleString([], tz ? { ...o, timeZone: tz } : o); } catch { return d.toLocaleString([], o); }
}
function gap(m: number | null): string {
    if (m === null || m === undefined) return '';
    const a = Math.abs(m); const sign = m < 0 ? '−' : '';
    return sign + (a < 90 ? `${a} min` : a < 48 * 60 ? `${(a / 60).toFixed(1)} h` : `${(a / 1440).toFixed(1)} d`);
}
const WORD: Record<string, string> = { waiting: 'waiting', missing: 'not recorded', now: 'in progress' };

/**
 * ⭐ F212 — THE LOADING / UNLOADING STEPS ARE BOTH THE CIRCLE, AND IT MOVES.
 * > Pete, 2026-10-01: *"The journey should be dynamic, meaning in the loading / unloading
 * > portion, it should have the circle icon for loading/unloading - actually they same across
 * > all the journey items in our operations pipeline screens too."*
 * Started and finished both draw the board's work mark (the ring and the truck): spinning while
 * the work is happening ('now'), a closed ring once it is finished, still and open before. The
 * finished step used to draw a tick, so a load being worked showed a tick for the step still in
 * progress. Pipeline's jyIcon draws the same three.
 */
function WorkStepMark({ s }: { s: JourneyStepDto }) {
    const closed = s.icon === 'worked' && (s.state === 'done' || s.state === 'late');
    if (closed) {
        return (
            <span className="relative inline-flex h-[22px] w-[22px]" title={s.label} aria-hidden="true">
                <svg className="absolute inset-0 h-full w-full" viewBox="0 0 48 48">
                    <circle cx="24" cy="24" r="19" fill="none" stroke="currentColor" strokeWidth={3.5} />
                </svg>
                <Truck className="absolute left-[23%] top-[23%] h-[54%] w-[54%]" strokeWidth={2.4} />
            </span>
        );
    }
    return (
        <WorkTruckMark
            wrapClassName="relative inline-flex h-[22px] w-[22px]"
            ringClassName={cn('absolute inset-0 h-full w-full', s.state === 'now' && 'animate-spin motion-reduce:[animation-duration:8s]')}
            truckClassName="absolute left-[23%] top-[23%] h-[54%] w-[54%]"
            ringStyle={s.state === 'now' ? { animationDuration: `${WORK_MARK_SPIN_SECONDS}s` } : undefined}
            title={s.label}
        />
    );
}

function StepIcon({ s, pct }: { s: JourneyStepDto; pct: number | null }) {
    const cls = 'h-[18px] w-[18px]';
    if (s.icon === 'working' || s.icon === 'worked') return <WorkStepMark s={s} />;
    if (s.icon === 'inventory' && pct !== null) {
        const p = Math.max(0, Math.min(100, pct)); const c = 2 * Math.PI * 10;
        return (
            <span className="relative inline-flex h-[24px] w-[24px]">
                <svg className="absolute inset-0 h-full w-full" viewBox="0 0 24 24" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeOpacity={0.25} strokeWidth={2.5} />
                    <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth={2.5}
                            strokeDasharray={`${(c * p / 100).toFixed(2)} ${c.toFixed(2)}`} transform="rotate(-90 12 12)" />
                </svg>
                <Package className="absolute left-[25%] top-[25%] h-[50%] w-[50%]" />
            </span>
        );
    }
    switch (s.icon) {
        case 'truck': case 'departed': return <Truck className={cls} />;
        case 'driver': return <User className={cls} />;
        case 'door': return <DoorOpen className={cls} />;
        case 'po': return <ClipboardCheck className={cls} />;
        case 'ready': return <PackageCheck className={cls} />;
        case 'inventory': case 'picked': return <Package className={cls} />;
        default: return <FileText className={cls} />;
    }
}

const DOT: Record<string, string> = {
    done: 'bg-stone-900 text-stone-50',
    waiting: 'bg-stone-100 text-stone-400',
    missing: 'bg-white text-stone-400 border border-dashed border-stone-300',
    now: 'bg-amber-100 text-amber-800',
    late: 'bg-amber-700 text-white',
};

export function Timeline({ t, tz, compact = false }: { t: JourneyTimelineDto; tz: string | null; compact?: boolean }) {
    const pct = t.progress && t.progress.length ? t.progress[t.progress.length - 1].pct : null;
    const o = t.order;
    return (
        <div className="py-2 border-t first:border-t-0">
            {o && (
                <div className="text-xs mb-1">
                    <span className="font-semibold">{o.OrderNumber}</span>{' '}
                    <span className="text-muted-foreground">
                        {o.WmsSource} · {o.OrderType}{o.BusinessUnit ? ` · ${o.BusinessUnit}` : ''}{o.currentStatus ? ` · ${o.currentStatus}` : ''}
                    </span>
                    {o.isOwner === false && (
                        <span className="ml-1 rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">
                            counted on appointment {o.ownerAppointmentId ?? '—'}
                        </span>
                    )}
                </div>
            )}
            <div className="flex items-start overflow-x-auto pb-1">
                {t.steps.map((s, i) => (
                    <Fragment key={s.key}>
                        {i > 0 && (
                            <div className={cn('relative mt-[17px] h-0.5 min-w-[24px] flex-1',
                                s.state === 'waiting' ? 'bg-[repeating-linear-gradient(90deg,#d6d3d1_0_4px,transparent_4px_8px)]' : 'bg-stone-900')}>
                                <span className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] text-muted-foreground">{gap(s.sincePrevMin)}</span>
                            </div>
                        )}
                        <div className={cn('shrink-0 text-center text-[11px]', compact ? 'w-[88px]' : 'w-[100px]')} title={s.detail ?? s.label}>
                            <div className={cn('mx-auto mb-1 flex h-[34px] w-[34px] items-center justify-center rounded-full', DOT[s.state])}>
                                <StepIcon s={s} pct={s.key === 'putaway' ? pct : null} />
                            </div>
                            <div className="font-semibold leading-tight">{s.label}</div>
                            <div className="text-muted-foreground">{s.atUtc ? fmt(s.atUtc, tz) : <span className="text-stone-400">{WORD[s.state] ?? ''}</span>}</div>
                            {s.detail && <div className={cn('leading-tight', s.state === 'late' ? 'text-amber-700' : 'text-muted-foreground')}>{s.detail}</div>}
                            {s.extra && <div className="text-stone-400">{s.extra.label} {fmt(s.extra.atUtc, tz)}</div>}
                        </div>
                    </Fragment>
                ))}
            </div>
            {t.progress && t.progress.length > 1 && (
                <div className="text-[11px] text-muted-foreground">
                    {t.progress.map((p) => `${p.pct !== null ? `${p.pct.toLocaleString([], { maximumFractionDigits: 1 })}%` : p.qty.toLocaleString()} at ${fmt(p.atUtc, tz)}`).join(' · ')}
                </div>
            )}
            {t.verdict && (
                <div className="text-xs text-muted-foreground mt-1">
                    {t.verdict.kpi === 'DTS' ? 'Dock to stock' : 'On-time shipping'}: <span className="font-semibold text-foreground">{t.verdict.verdict}</span>
                    {t.verdict.deadlineUtc ? ` · due ${fmt(t.verdict.deadlineUtc, tz)}` : ''} — {t.verdict.reason}
                </div>
            )}
            {/* ⭐ F231: an outbound order's lines show picked / shipped / short (it has no receipts) */}
            {t.lines && t.lines.length > 0 && t.lines.some((l) => l.outbound) && (
                <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-muted-foreground">{t.lines.length} line{t.lines.length === 1 ? '' : 's'}</summary>
                    <table className="mt-1 w-full text-xs">
                        <thead><tr className="text-left text-muted-foreground">
                            <th className="font-normal">Line</th><th className="font-normal">Item</th><th className="text-right font-normal">Ordered</th>
                            <th className="text-right font-normal">Picked</th><th className="text-right font-normal">Shipped</th><th className="text-right font-normal">Short</th><th className="pl-2 font-normal">Status</th>
                        </tr></thead>
                        <tbody>
                            {t.lines.map((l, i) => (
                                <tr key={`${l.lineNumber}-${i}`} className="border-t">
                                    <td>{l.lineNumber}</td><td>{l.itemNumber ?? '—'}</td>
                                    <td className="text-right">{l.ordered === null ? '—' : l.ordered.toLocaleString()}</td>
                                    <td className="text-right">{l.outbound?.picked == null ? '—' : l.outbound.picked.toLocaleString()}</td>
                                    <td className="text-right">{l.outbound?.shipped == null ? '—' : l.outbound.shipped.toLocaleString()}</td>
                                    <td className="text-right">{l.outbound?.short == null || l.outbound.short === 0 ? '—' : l.outbound.short.toLocaleString()}</td>
                                    <td className="pl-2">{l.outbound?.status ?? '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}
            {t.lines && t.lines.length > 0 && !t.lines.some((l) => l.outbound) && (
                <details className="mt-1">
                    <summary className="cursor-pointer text-xs text-muted-foreground">{t.lines.length} line{t.lines.length === 1 ? '' : 's'}</summary>
                    <table className="mt-1 w-full text-xs">
                        <thead><tr className="text-left text-muted-foreground">
                            <th className="font-normal">Line</th><th className="font-normal">Item</th><th className="text-right font-normal">Ordered</th>
                            <th className="text-right font-normal">Received</th><th className="pl-2 font-normal">Last received</th><th className="font-normal">Where</th>
                        </tr></thead>
                        <tbody>
                            {t.lines.map((l, i) => (
                                <tr key={`${l.lineNumber}-${i}`} className="border-t">
                                    <td>{l.lineNumber}</td><td>{l.itemNumber ?? '—'}</td>
                                    <td className="text-right">{l.ordered === null ? '—' : l.ordered.toLocaleString()}</td>
                                    <td className="text-right">{l.received.toLocaleString()}</td>
                                    <td className="pl-2">{l.lastReceivedUtc ? fmt(l.lastReceivedUtc, tz) : '—'}</td>
                                    <td>{l.locations.join(', ') || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </details>
            )}
        </div>
    );
}

/**
 * ⭐ F212 — the timelines alone, for the portals' row under each appointment (portal data comes
 * from Pipeline's portal cut: times only, no verdicts, a client's own orders).
 */
export function JourneyTimelines({ data, compact = false }: { data: JourneyDto; compact?: boolean }) {
    return (
        <>
            {data.timelines.map((t, i) => <Timeline key={t.order ? `${t.order.OrderType}|${t.order.OrderNumber}` : `truck-${i}`} t={t} tz={data.timezone} compact={compact} />)}
            {data.notes.map((n) => <div key={n} className="text-[11px] text-muted-foreground">{n}</div>)}
        </>
    );
}

export function JourneyPanel({ appointmentId, getToken }: { appointmentId: number; getToken: () => Promise<string> }) {
    const [data, setData] = useState<JourneyDto | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);

    useEffect(() => { setData(null); setError(null); setOpen(false); }, [appointmentId]);
    useEffect(() => {
        if (!open || data) return;
        let alive = true;
        (async () => {
            try {
                const r = await getAppointmentJourney(await getToken(), appointmentId);
                if (alive) setData(r);
            } catch (e) {
                if (alive) setError(e instanceof Error ? e.message : 'Could not read the journey.');
            }
        })();
        return () => { alive = false; };
    }, [open, data, appointmentId, getToken]);

    return (
        <div className="col-span-2 rounded-md border px-3 py-2">
            <button type="button" className="text-sm font-medium" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
                {open ? '▾' : '▸'} The journey
            </button>
            {open && (
                <div className="mt-1">
                    {error && <div className="text-xs text-muted-foreground">The journey could not be read: {error}</div>}
                    {!error && !data && <div className="h-10 animate-pulse rounded bg-muted" />}
                    {data && (
                        <>
                            <div className="text-[11px] text-muted-foreground">
                                Times in the facility's zone{data.timezone ? ` (${data.timezone})` : ''}. Grey = not reached · dashed = no time recorded · orange = past its target.
                            </div>
                            {data.timelines.map((t, i) => <Timeline key={t.order ? `${t.order.OrderType}|${t.order.OrderNumber}` : `truck-${i}`} t={t} tz={data.timezone} />)}
                            {data.notes.map((n) => <div key={n} className="text-[11px] text-muted-foreground">{n}</div>)}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
